import { useEffect, useRef } from "react";
import { createChart, CrosshairMode } from "lightweight-charts";

const base = (el, height) =>
  createChart(el, {
    height,
    layout: { background: { color: "transparent" }, textColor: "#94a3b8", fontFamily: "JetBrains Mono" },
    grid: { vertLines: { color: "rgba(51,65,85,0.25)" }, horzLines: { color: "rgba(51,65,85,0.25)" } },
    crosshair: { mode: CrosshairMode.Normal },
    rightPriceScale: { borderColor: "rgba(51,65,85,0.6)" },
    timeScale: { borderColor: "rgba(51,65,85,0.6)", timeVisible: true },
    autoSize: true,
  });

const UP = "#00d6a8", DOWN = "#ef4655";

export function CandleChart({ data, overlays, digits = 5 }) {
  const mainRef = useRef(null), rsiRef = useRef(null), macdRef = useRef(null);

  useEffect(() => {
    if (!data) return;
    const main = base(mainRef.current, 420), rsi = base(rsiRef.current, 120), macd = base(macdRef.current, 120);
    const fmt = { type: "price", precision: digits, minMove: 1 / 10 ** digits };
    const c = main.addCandlestickSeries({ upColor: UP, downColor: DOWN, borderVisible: false, wickUpColor: UP, wickDownColor: DOWN, priceFormat: fmt });
    c.setData(data.candles);
    const ind = data.indicators;
    const lines = { ema20: "#3b82f6", ema50: "#f5b82e", ema200: "#e879f9", sma20: "#94a3b8", sma50: "#64748b", bb_upper: "rgba(148,163,184,0.5)", bb_lower: "rgba(148,163,184,0.5)" };
    Object.entries(lines).forEach(([k, color]) => {
      if (!overlays[k.startsWith("bb") ? "bb" : k]) return;
      main.addLineSeries({ color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, priceFormat: fmt }).setData(ind[k]);
    });
    const r = rsi.addLineSeries({ color: "#a78bfa", lineWidth: 1.5 });
    r.setData(ind.rsi);
    r.createPriceLine({ price: 70, color: DOWN, lineStyle: 2, lineWidth: 1 });
    r.createPriceLine({ price: 30, color: UP, lineStyle: 2, lineWidth: 1 });
    macd.addHistogramSeries({ priceFormat: fmt }).setData(ind.macd_hist.map((p) => ({ ...p, color: p.value >= 0 ? "rgba(0,214,168,0.6)" : "rgba(239,70,85,0.6)" })));
    macd.addLineSeries({ color: "#3b82f6", lineWidth: 1, priceFormat: fmt }).setData(ind.macd);
    macd.addLineSeries({ color: "#f5b82e", lineWidth: 1, priceFormat: fmt }).setData(ind.macd_signal);
    const charts = [main, rsi, macd];
    charts.forEach((ch) => ch.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (!range) return;
      charts.forEach((o) => o !== ch && o.timeScale().setVisibleLogicalRange(range));
    }));
    main.timeScale().fitContent();
    return () => charts.forEach((ch) => ch.remove());
  }, [data, overlays, digits]);

  return (
    <div className="space-y-1" data-testid="candle-chart">
      <div ref={mainRef} className="h-[420px] w-full" />
      <div className="label px-1">RSI 14</div>
      <div ref={rsiRef} className="h-[120px] w-full" data-testid="rsi-chart" />
      <div className="label px-1">MACD 12/26/9</div>
      <div ref={macdRef} className="h-[120px] w-full" data-testid="macd-chart" />
    </div>
  );
}
